"use client";

import { usePathname } from "next/navigation";
import { ZoneLink } from "@/components/layout/zone-link";
import { buttonClass } from "@/components/ui/button";
import { useSession } from "@/lib/auth/client";
import { useHydrated } from "@/lib/hooks/use-hydrated";
import type { Locale } from "@/lib/i18n/config";
import type { Dictionary } from "@/lib/i18n/get-dictionary";
import { authHref } from "@/lib/jobs/auth-return";
import { useAuthLinkClick } from "@/lib/jobs/use-auth-link";

type Props = {
	locale: Locale;
	t: Dictionary;
};

/**
 * Compact "Daftar" CTA shown <sm for guests only.
 *
 * PR-3 (2026-08-10, Task 3.3): the guest mobile header row was logo + locale
 * switcher + hamburger on every single page — no visible conversion path
 * unless the guest opened the drawer. Mobile outconverts desktop on
 * feed→apply (14.2% vs 12.2%), so the signup nudge belongs where mobile
 * traffic already is, not one tap deeper.
 *
 * Stays client-only and mount-gated for the same reason as NavbarAuth (the
 * ≥sm counterpart) and NewJobsBadge: reading auth server-side would force
 * this shared-layout component's route dynamic (incident 2026-05-30).
 */
export function MobileSignupButton({ locale, t }: Props) {
	const { data: session, isPending } = useSession();
	const pathname = usePathname();
	const authLinkClick = useAuthLinkClick(locale);
	const hydrated = useHydrated();

	if (!hydrated || isPending || session?.user) return null;

	return (
		<ZoneLink
			href={authHref("signup", locale, pathname)}
			onClick={authLinkClick}
			className={buttonClass({
				size: "xs",
				shape: "pill",
				className: "h-9 shrink-0 whitespace-nowrap",
			})}
		>
			{t.signup || (locale === "id" ? "Daftar" : "Sign up")}
		</ZoneLink>
	);
}
